import { Link } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import type { DerivedStockPart, Product } from '@/api/types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

/**
 * The stock reading of a dish, variant or not-tracked tile (CHG-2026-09-stock-usage-and-price-twins,
 * D8, task 11.5), derived from what `GET /products/lookup?includeStock=true` returns for it. Null
 * for a plain article — its tile keeps the number it always showed.
 */
export interface DerivedTileStock {
  kind: 'dish' | 'variant' | 'not_tracked'
  /** No tracked part: the tile shows no number, like an article nobody tracks. */
  unlimited: boolean
  inStoreQty: number | null
  onlineQty: number | null
  parts: Array<DerivedStockPart>
  limitingSku: string | null
  /** The chip under the name: part count or "Variante" with the limiting part. */
  chip: { key: string; values: Record<string, unknown> } | null
  /** Set when the tile reads 0: the banner naming the part that is empty (or too short). */
  emptyPartLabel: { key: string; name: string } | null
}

export function derivedTileStock(product: Product): DerivedTileStock | null {
  const kind = product.stockKind
  if (kind !== 'dish' && kind !== 'variant' && kind !== 'not_tracked') {
    return null
  }
  const unlimited = product.unlimited === true
  const parts = product.parts ?? []
  const inStoreQty = unlimited ? null : (product.inStoreQty ?? null)
  const limitingSku = product.inStoreLimitingSku ?? null
  const limitingPart = parts.find((part) => part.sku === limitingSku)
  const limitingName =
    product.inStoreLimitingName ?? limitingPart?.name ?? limitingSku

  let chip: DerivedTileStock['chip'] = null
  if (kind === 'not_tracked') {
    chip = { key: 'tileStock.notTracked', values: {} }
  } else if (unlimited) {
    chip = { key: 'tileStock.unlimited', values: {} }
  } else if (kind === 'dish') {
    chip = limitingName
      ? {
          key: 'tileStock.dishChip',
          values: { count: parts.length, name: limitingName },
        }
      : { key: 'tileStock.dishChipNoLimit', values: { count: parts.length } }
  } else {
    chip = limitingName
      ? { key: 'tileStock.variantChip', values: { name: limitingName } }
      : { key: 'tileStock.variantChipNoLimit', values: {} }
  }

  let emptyPartLabel: DerivedTileStock['emptyPartLabel'] = null
  if (inStoreQty === 0 && limitingName) {
    // "leer" only when the part is really gone; a remainder smaller than one portion "reicht nicht".
    emptyPartLabel = {
      key:
        limitingPart && limitingPart.availableQty > 0
          ? 'tileStock.partShort'
          : 'tileStock.partEmpty',
      name: limitingName,
    }
  }

  return {
    kind,
    unlimited,
    inStoreQty,
    onlineQty: unlimited ? null : (product.onlineQty ?? null),
    parts,
    limitingSku,
    chip,
    emptyPartLabel,
  }
}

/** "0,5 Kiste" — the part's unit comes with the lookup (phase B4); absent on an older backend. */
function withUnit(qty: number, unit: string | undefined): string {
  return unit ? `${formatStockDisplay(qty)} ${unit}` : formatStockDisplay(qty)
}

/**
 * The tile's number as a button: tapping it opens the per-part breakdown with the online number
 * and a link to the product's stock usage. A variant links to its source, which owns the usage.
 */
export function TileStockBreakdown({
  product,
  reading,
}: {
  product: Product
  reading: DerivedTileStock
}) {
  const { t } = useTranslation('pos-layout')
  if (reading.inStoreQty == null) return null
  const usageSku = product.variantSourceSku ?? product.sku

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('tileStock.openBreakdown')}
          className={cn(
            'rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums underline decoration-dotted underline-offset-2',
            reading.inStoreQty === 0
              ? 'bg-pos-tile-alert text-pos-tile-alert-foreground'
              : 'opacity-75 hover:opacity-100',
          )}
        >
          {formatStockDisplay(reading.inStoreQty)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 space-y-3">
        <div className="space-y-0.5">
          <p className="text-sm font-semibold">{product.name}</p>
          <p className="text-sm text-muted-foreground">
            {t('tileStock.enoughInStore', {
              qty: formatStockDisplay(reading.inStoreQty),
            })}
          </p>
        </div>
        {reading.parts.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 font-medium">
                  {t('tileStock.columnPart')}
                </th>
                <th className="py-1 text-right font-medium">
                  {t('tileStock.columnPerUnit')}
                </th>
                <th className="py-1 text-right font-medium">
                  {t('tileStock.columnStock')}
                </th>
                <th className="py-1 text-right font-medium">
                  {t('tileStock.columnEnough')}
                </th>
              </tr>
            </thead>
            <tbody>
              {reading.parts.map((part) => {
                const limiting = part.sku === reading.limitingSku
                return (
                  <tr
                    key={part.sku}
                    className={cn(
                      'border-t',
                      limiting &&
                        'bg-amber-50 font-semibold dark:bg-amber-950/40',
                    )}
                  >
                    <td className="py-1">
                      {part.name ?? part.sku}
                      {limiting ? ` · ${t('tileStock.limiting')}` : ''}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {withUnit(part.qtyPerUnit, part.unit)}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {withUnit(part.availableQty, part.unit)}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {formatStockDisplay(part.inStoreQty)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        <p className="rounded-md bg-muted px-2 py-1.5 text-sm">
          {reading.onlineQty == null
            ? t('tileStock.onlineUnlimited')
            : t('tileStock.online', {
                qty: formatStockDisplay(reading.onlineQty),
              })}
        </p>
        <Link
          to="/products/$sku"
          params={{ sku: usageSku }}
          search={{ tab: 'stockUsage' }}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t('tileStock.editUsage')}
        </Link>
      </PopoverContent>
    </Popover>
  )
}
